import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { Link } from "react-router";
import { ArrowRight, BookOpen, CheckCircle2, ClipboardCheck, FilePenLine, History, PenLine, Puzzle, Search } from "lucide-react";
import { api, type Coverage, type CurriculumTree } from "../lib/api";
import { useStudio } from "../lib/context";
import { Avatar, Badge, Empty, ErrorBox, Loading, ProgressBar, Ring, Seg, timeAgo, type Progress } from "../components/ui";

type Notion = CurriculumTree[number]["chapters"][number]["notions"][number] & { chapter: string; ordinal: number };

export function progressOf(statuses: Coverage[]): Progress {
  return {
    ok: statuses.filter((s) => s === "VALIDATED").length,
    review: statuses.filter((s) => s === "IN_REVIEW").length,
    draft: statuses.filter((s) => s === "DRAFT" || s === "AI_DRAFT" || s === "REJECTED").length,
    total: statuses.length,
  };
}

export function Dashboard() {
  const { countryId, languages, teacher, recent, setPaletteOpen } = useStudio();
  const q = useQuery({ queryKey: ["dashboard", countryId], queryFn: () => api.dashboard(countryId) });
  const tree = useQuery({ queryKey: ["curricula", countryId], queryFn: () => api.curricula(countryId) });
  const [lang, setLang] = useState(languages[0]?.code ?? "fr");
  const d = q.data;
  const cv = tree.data?.[0];
  const notions = useMemo<Notion[]>(() => cv?.chapters.flatMap((ch) => ch.notions.map((n) => ({ ...n, chapter: ch.title, ordinal: ch.ordinal }))) ?? [], [cv]);
  const cov = (n: Notion, l: string) => n.coverage[l] ?? "MISSING";
  const ex = (s: string) => d?.exercises.find((x) => x.status === s)?.count ?? 0;
  const first = (teacher?.displayName ?? "").replace(/\(.*?\)/g, "").trim().split(/\s+/)[0] ?? "";
  const instr = languages[0]?.code ?? "fr";
  const toFix = notions.filter((n) => languages.some((l) => cov(n, l.code) === "REJECTED"));
  const toWrite = notions.filter((n) => cov(n, instr) === "MISSING" || cov(n, instr) === "AI_DRAFT").slice(0, 5);
  const today = new Date().toLocaleDateString("fr-FR", { weekday: "long", day: "numeric", month: "long" });

  return (
    <div className="stack" style={{ gap: 18 }}>
      <section className="hero appear">
        <div className="kicker" style={{ color: "rgba(255,255,255,0.7)" }}>{today}</div>
        <h1><span className="sr-only">Tableau de bord — </span>Bonjour {first || "et bienvenue"}</h1>
        <p>
          {d ? <>{d.validatedByLang.find((x) => x.lang === instr)?.count ?? 0} leçons validées sur {d.notions} en {languages[0]?.name.toLowerCase() ?? "français"}
            {d.reviewQueue.length > 0 ? <> · <b style={{ color: "#fff" }}>{d.reviewQueue.length} contenu{d.reviewQueue.length > 1 ? "s" : ""} à relire</b></> : " · rien à relire"}.</> : "Chargement de l'avancement…"}
        </p>
        <div className="row wrap" style={{ marginTop: 18 }}>
          <Link to="/programme" className="btn light"><BookOpen size={16} />Parcourir les notions</Link>
          <button className="btn ghost" onClick={() => setPaletteOpen(true)}><Search size={16} />Trouver une notion<span className="kbd">/</span></button>
        </div>
      </section>

      {(q.isLoading || tree.isLoading) && <Loading />}
      {q.error && <ErrorBox error={q.error} onRetry={() => q.refetch()} />}
      {d && (
        <div className="grid stats appear">
          {languages.map((l) => {
            const p = progressOf(notions.map((n) => cov(n, l.code)));
            const v = d.validatedByLang.find((x) => x.lang === l.code)?.count ?? 0;
            return (
              <div key={l.code} className="card lang-stat">
                <Ring value={d.notions ? v / d.notions : 0}>{d.notions ? Math.round((v / d.notions) * 100) : 0}%</Ring>
                <div className="stat" style={{ minWidth: 0 }}>
                  <div className="v">{v}<span className="muted" style={{ fontSize: 15 }}> / {d.notions}</span></div>
                  <div className="l">Leçons validées · {l.nativeName ?? l.name}</div>
                  <div className="tiny muted">{p.review} en relecture · {p.draft} en brouillon</div>
                </div>
              </div>
            );
          })}
          <div className="card lang-stat">
            <span className="icon-tile amber" style={{ width: 56, height: 56, borderRadius: 16 }}><Puzzle size={24} /></span>
            <div className="stat">
              <div className="v">{ex("VALIDATED")}</div>
              <div className="l">Gabarits d'exercices validés</div>
              <div className="tiny muted">{ex("IN_REVIEW")} en relecture</div>
            </div>
          </div>
        </div>
      )}

      {d && (
        <div className="dash">
          <div className="stack" style={{ gap: 16 }}>
            <section className="card appear" aria-labelledby="h-review">
              <div className="section-title">
                <span className="icon-tile amber"><ClipboardCheck size={18} /></span>
                <div><h2 id="h-review">À relire</h2><p>Contenus soumis par d'autres enseignants, dans vos langues de relecture</p></div>
              </div>
              {d.reviewQueue.length === 0
                ? <Empty icon={<CheckCircle2 size={20} />} title="Rien à relire pour l'instant.">Les contenus soumis apparaîtront ici.</Empty>
                : d.reviewQueue.map((r) => (
                  <Link key={r.contentId} className="list-row review-row" to={`/notions/${r.notionId}?lang=${r.lang}&review=${r.contentId}`}>
                    <Avatar name={r.author} small />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <b style={{ display: "block" }}>{r.notionTitle}</b>
                      <span className="tiny muted">{r.author} · version {r.version} · {timeAgo(r.submittedAt)}</span>
                    </div>
                    <span className="badge plain b-DRAFT mono">{r.lang.toUpperCase()}</span>
                    <span className="btn sm ghost">Relire<ArrowRight size={14} /></span>
                  </Link>
                ))}
            </section>

            {cv && (
              <section className="card appear" aria-labelledby="h-progress">
                <div className="section-title">
                  <div style={{ flex: 1 }}><h2 id="h-progress">Avancement par chapitre</h2><p>{cv.subject} · {cv.grade}</p></div>
                  {languages.length > 1 && <Seg label="Langue" value={lang} onChange={setLang} options={languages.map((l) => ({ value: l.code, label: l.nativeName ?? l.name }))} />}
                </div>
                <div className="legend" style={{ marginBottom: 8 }}><span><i className="p-ok" />Validées</span><span><i className="p-review" />En relecture</span><span><i className="p-draft" />Brouillons</span></div>
                {cv.chapters.map((ch) => {
                  const p = progressOf(ch.notions.map((n) => n.coverage[lang] ?? "MISSING"));
                  return (
                    <Link key={ch.id} to={`/programme#ch-${ch.ordinal}`} className="ch-bar" style={{ color: "var(--text)" }} title={ch.title}>
                      <span className="num">{ch.ordinal}</span>
                      <div style={{ minWidth: 0 }}>
                        <div className="small" style={{ fontWeight: 600, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", marginBottom: 5 }}>{ch.title}</div>
                        <ProgressBar p={p} />
                      </div>
                      <span className="small muted" style={{ textAlign: "right", fontWeight: 700 }}>{p.ok}/{p.total}</span>
                    </Link>
                  );
                })}
              </section>
            )}
          </div>

          <div className="stack" style={{ gap: 16 }}>
            <section className="card appear" aria-labelledby="h-recent">
              <div className="section-title"><span className="icon-tile ink"><History size={18} /></span><h2 id="h-recent">Reprendre</h2></div>
              {recent.length === 0
                ? <p className="small muted">Les notions que vous ouvrez apparaîtront ici.</p>
                : recent.map((r) => (
                  <Link key={r.id} to={`/notions/${r.id}`} className="list-row">
                    <PenLine size={16} color="var(--text-3)" />
                    <span style={{ flex: 1, minWidth: 0, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{r.title}</span>
                    <span className="tiny muted">{timeAgo(r.at)}</span>
                  </Link>
                ))}
            </section>

            {toFix.length > 0 && (
              <section className="card appear" aria-labelledby="h-fix">
                <div className="section-title"><span className="icon-tile bad"><FilePenLine size={18} /></span><h2 id="h-fix">Corrections demandées</h2></div>
                {toFix.slice(0, 5).map((n) => (
                  <Link key={n.id} to={`/notions/${n.id}`} className="list-row">
                    <span style={{ flex: 1, minWidth: 0, fontWeight: 600 }}>{n.title}</span><Badge status="REJECTED" />
                  </Link>
                ))}
              </section>
            )}

            {toWrite.length > 0 && (
              <section className="card appear" aria-labelledby="h-write">
                <div className="section-title"><span className="icon-tile"><PenLine size={18} /></span><div><h2 id="h-write">Leçons à écrire</h2><p>Sans version {languages[0]?.name.toLowerCase() ?? ""} rédigée</p></div></div>
                {toWrite.map((n) => (
                  <Link key={n.id} to={`/notions/${n.id}`} className="list-row">
                    <span className="ch-bar" style={{ display: "contents" }}><span className="num">{n.ordinal}</span></span>
                    <span style={{ flex: 1, minWidth: 0, fontWeight: 600 }}>{n.title}</span>
                    <ArrowRight size={15} color="var(--text-3)" />
                  </Link>
                ))}
              </section>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
